import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert, Button } from 'react-bootstrap';
import { youtubeConfig } from '../config/youtubeConfig';

function YouTubeFeed() {
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');
  
  // Use channel username from config - note: using username instead of ID
  const channelUsername = 'beniaminkwak'; // Using the handle instead of ID
  
  useEffect(() => {
    // Fetch videos from YouTube API
    const fetchYouTubeVideos = async () => {
      try {
        setLoading(true);
        
        let debugLog = '';
        
        // YouTube API key from environment variable
        const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;
        console.log('API Key available:', !!apiKey); // Log if API key exists without revealing it
        
        debugLog += `API Key exists: ${!!apiKey}\n`;
        
        if (!apiKey) {
          console.warn('YouTube API key not found. Using fallback videos.');
          debugLog += 'No API key found in environment variables.';
          setDebugInfo(debugLog);
          setVideos(youtubeConfig.fallbackVideos);
          setUsingFallback(true);
          setLoading(false);
          return;
        }
        
        // First find the channel by username instead of ID
        debugLog += 'Fetching channel by username...\n';
        console.log('Fetching channel by username...');
        const channelSearchResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${channelUsername}&type=channel&key=${apiKey}`,
          {
            headers: {
              'Accept': 'application/json'
            }
          }
        );
        
        debugLog += `Channel search response status: ${channelSearchResponse.status}\n`;
        
        if (!channelSearchResponse.ok) {
          console.error('Channel search response not OK:', channelSearchResponse.status, channelSearchResponse.statusText);
          debugLog += `Failed to fetch channel by username: ${channelSearchResponse.status} ${channelSearchResponse.statusText}`;
          setDebugInfo(debugLog);
          throw new Error(`Failed to fetch channel by username: ${channelSearchResponse.status} ${channelSearchResponse.statusText}`);
        }
        
        const channelSearchData = await channelSearchResponse.json();
        debugLog += `Channel search data received\n`;
        
        if (!channelSearchData.items || channelSearchData.items.length === 0) {
          console.error('No channels found by this username:', channelSearchData);
          debugLog += `No channels found by username: ${channelUsername}`;
          setDebugInfo(debugLog);
          throw new Error(`No channels found by username: ${channelUsername}`);
        }
        
        // Get the actual channel ID from search results
        const foundChannelId = channelSearchData.items[0].id.channelId;
        debugLog += `Found channel ID: ${foundChannelId}\n`;
        console.log('Found channel ID:', foundChannelId);
        
        // Now get the uploads playlist ID
        const channelResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/channels?part=contentDetails&id=${foundChannelId}&key=${apiKey}`,
          {
            headers: {
              'Accept': 'application/json'
            }
          }
        );
        
        debugLog += `Channel response status: ${channelResponse.status}\n`;
        
        if (!channelResponse.ok) {
          console.error('Channel response not OK:', channelResponse.status, channelResponse.statusText);
          debugLog += `Failed to fetch channel data: ${channelResponse.status} ${channelResponse.statusText}`;
          setDebugInfo(debugLog);
          throw new Error(`Failed to fetch channel data: ${channelResponse.status} ${channelResponse.statusText}`);
        }
        
        const channelData = await channelResponse.json();
        debugLog += 'Channel data received\n';
        
        if (!channelData.items || channelData.items.length === 0) {
          console.error('No channel items found:', channelData);
          debugLog += `No channel items found: ${JSON.stringify(channelData)}`;
          setDebugInfo(debugLog);
          throw new Error('No channel data found');
        }
        
        const uploadsPlaylistId = channelData.items[0].contentDetails.relatedPlaylists.uploads;
        debugLog += `Uploads playlist ID: ${uploadsPlaylistId}\n`;
        
        // Fetch videos from the uploads playlist
        debugLog += 'Fetching videos...\n';
        const videosResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=9&playlistId=${uploadsPlaylistId}&key=${apiKey}`,
          {
            headers: {
              'Accept': 'application/json'
            }
          }
        );
        
        debugLog += `Videos response status: ${videosResponse.status}\n`;
        
        if (!videosResponse.ok) {
          console.error('Videos response not OK:', videosResponse.status, videosResponse.statusText);
          debugLog += `Failed to fetch videos: ${videosResponse.status} ${videosResponse.statusText}`;
          setDebugInfo(debugLog);
          throw new Error(`Failed to fetch videos: ${videosResponse.status} ${videosResponse.statusText}`);
        }
        
        const videosData = await videosResponse.json();
        debugLog += `Videos data received, count: ${videosData.items?.length}\n`;
        
        // Transform the response into the format we need
        const formattedVideos = videosData.items.map(item => {
          const snippet = item.snippet;
          return {
            id: snippet.resourceId.videoId,
            title: snippet.title,
            thumbnail: snippet.thumbnails.high ? snippet.thumbnails.high.url : snippet.thumbnails.default.url,
            url: `https://www.youtube.com/watch?v=${snippet.resourceId.videoId}`,
            publishedAt: new Date(snippet.publishedAt).toLocaleDateString()
          };
        });
        
        debugLog += `Videos formatted successfully, count: ${formattedVideos.length}`;
        setDebugInfo(debugLog);
        setVideos(formattedVideos);
        setUsingFallback(false);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching YouTube videos:', err);
        console.warn('Using fallback videos due to API error.');
        setDebugInfo(prev => prev + `\nError: ${err.message}`);
        setVideos(youtubeConfig.fallbackVideos);
        setUsingFallback(true);
        setLoading(false);
      }
    };

    fetchYouTubeVideos();
  }, [channelUsername]);

  if (loading) {
    return (
      <section className="youtube-feed py-5 mt-3 text-center">
        <div className="d-inline-block position-relative mb-4">
          <h2 className="display-5 fw-bold mb-0">
            <i className="bi bi-youtube text-danger me-2"></i>
            Latest YouTube Tutorials
          </h2>
          <div className="position-absolute" style={{ 
            height: '10px', 
            width: '50%', 
            backgroundColor: '#ff0000',
            bottom: '-5px',
            left: '25%',
            borderRadius: '5px'
          }}></div>
        </div>
        <div className="py-5">
          <Spinner animation="border" variant="danger" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading videos from YouTube...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="youtube-feed py-5 mt-3">
        <div className="text-center mb-5">
          <div className="d-inline-block position-relative">
            <h2 className="display-5 fw-bold mb-0">
              <i className="bi bi-youtube text-danger me-2"></i>
              Latest YouTube Tutorials
            </h2>
            <div className="position-absolute" style={{ 
              height: '10px', 
              width: '50%', 
              backgroundColor: '#ff0000',
              bottom: '-5px',
              left: '25%',
              borderRadius: '5px'
            }}></div>
          </div>
        </div>
        
        <Alert variant="warning" className="mx-auto" style={{ maxWidth: '800px' }}>
          <Alert.Heading><i className="bi bi-exclamation-triangle me-2"></i> YouTube Connection Issue</Alert.Heading>
          <p className="mb-0">
            We're having trouble connecting to YouTube right now. You can still visit the channel directly.
          </p>
        </Alert>
        
        <div className="text-center mt-4">
          <a 
            href={youtubeConfig.channelUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn btn-danger"
          >
            <i className="bi bi-youtube me-2"></i>
            Visit YouTube Channel
          </a>
        </div>
      </section>
    );
  }

  return (
    <section id="youtube" className="youtube-feed py-5 mt-3">
      <div className="text-center mb-5">
        <h2 className="display-5 fw-bold mb-3" style={{ color: '#0078D4' }}>
          <i className="bi bi-youtube text-danger me-2"></i>
          Latest YouTube Tutorials
        </h2>
        <p className="lead text-muted" style={{ maxWidth: '700px', margin: '0 auto' }}>
          Practical Azure & DevOps tutorials with real-world examples
        </p>
        <div className="divider mx-auto mt-4" style={{
          width: '80px',
          height: '4px',
          background: 'linear-gradient(90deg, #FF0000, #FF4444)',
          borderRadius: '2px'
        }}></div>
        
        {usingFallback && (
          <div className="mt-3">
            <small className="text-muted">
              <i className="bi bi-info-circle me-1"></i>
              Using saved videos. Visit my <a href={youtubeConfig.channelUrl} target="_blank" rel="noopener noreferrer" className="text-danger">YouTube channel</a> for the latest content.
            </small>
          </div>
        )}
      </div>
      
      <Row className="g-4 justify-content-center">
        {videos.map((video) => (
          <Col key={video.id} md={6} lg={4}>
            <a 
              href={video.url}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-decoration-none"
            >
              <Card 
                className="border-0 h-100 video-card" 
                style={{
                  transform: hoveredVideo === video.id ? 'translateY(-10px) scale(1.02)' : 'translateY(0) scale(1)',
                  transition: 'transform 0.3s ease',
                  borderRadius: '15px',
                  overflow: 'hidden',
                  boxShadow: hoveredVideo === video.id ? 
                    '0 15px 30px rgba(0,0,0,0.15)' : 
                    '0 5px 15px rgba(0,0,0,0.08)'
                }}
                onMouseEnter={() => setHoveredVideo(video.id)}
                onMouseLeave={() => setHoveredVideo(null)}
              >
                <div className="position-relative overflow-hidden" style={{ paddingTop: '56.25%', background: '#000' }}>
                  <Card.Img 
                    variant="top" 
                    src={video.thumbnail}
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <div className="position-absolute top-0 start-0 w-100 h-100" 
                      style={{ 
                        background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)',
                        opacity: hoveredVideo === video.id ? 1 : 0.5,
                        transition: 'opacity 0.3s ease'
                      }} 
                  />
                  
                  <div className="position-absolute top-50 start-50 translate-middle" 
                      style={{ 
                        opacity: hoveredVideo === video.id ? 1 : 0,
                        transition: 'opacity 0.3s ease'
                      }}>
                    <div className="bg-danger text-white rounded-circle d-flex align-items-center justify-content-center" 
                        style={{ 
                          width: '60px', 
                          height: '60px',
                          boxShadow: '0 0 15px rgba(0,0,0,0.5)'
                        }}>
                      <i className="bi bi-play-fill" style={{ fontSize: '2rem' }}></i>
                    </div>
                  </div>
                </div>
                <Card.Body>
                  <Card.Title className="fw-bold text-dark">{video.title}</Card.Title>
                  {video.publishedAt && (
                    <div className="mt-2">
                      <small className="text-muted">{video.publishedAt}</small>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </a>
          </Col>
        ))}
      </Row>
      
      {videos.length === 0 && (
        <div className="text-center py-5">
          <p>No videos found. Check back soon!</p>
        </div>
      )}
      
      <div className="text-center mt-5">
        <a 
          href={youtubeConfig.channelUrl}
          target="_blank" 
          rel="noopener noreferrer"
          className="btn btn-danger btn-lg"
          style={{
            borderRadius: '30px',
            padding: '10px 25px',
            boxShadow: '0 5px 15px rgba(220, 53, 69, 0.3)'
          }}
        >
          <i className="bi bi-collection-play me-2"></i>
          Watch All Videos on YouTube
        </a>
      </div>
      
      {process.env.NODE_ENV === 'development' && debugInfo && usingFallback && (
        <div className="mt-5 p-3 bg-light rounded">
          <h5>Debug Information (only visible in development)</h5>
          <pre className="bg-dark text-light p-3 rounded" style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
            {debugInfo}
          </pre>
          <div className="mt-3">
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={() => window.open(`https://console.cloud.google.com/apis/credentials`, '_blank')}
            >
              Check API Key Settings
            </Button>
            <small className="ms-3 text-muted">
              Ensure your API key has YouTube Data API v3 enabled and proper HTTP referrer restrictions
            </small>
          </div>
        </div>
      )}
    </section>
  );
}

export default YouTubeFeed;